import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api } from '@/api/client'

const TOKEN_REFRESH_RETRY_DELAY_MS = 30_000
const LOGOUT_REFRESH_WAIT_MS = 5_000

/** Decode JWT payload without a library */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const base64 = token.split('.')[1]
    if (!base64) return null
    const json = atob(base64.replace(/-/g, '+').replace(/_/g, '/'))
    return JSON.parse(json)
  } catch {
    return null
  }
}

export interface UserAttributes {
  favorite_workflows?: Record<string, string[]>  // e.g., {"n8n": ["workflow-id-1", "workflow-id-2"]}
  [key: string]: unknown  // Allow other attributes
}

export interface User {
  id: number
  email: string
  name: string
  scopes: string[]
  attributes: UserAttributes
}

interface LoginResponse {
  access_token: string
  user: User
}

interface AzureStatusResponse {
  configured: boolean
  login_url: string | null
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const accessToken = ref<string | null>(localStorage.getItem('accessToken'))
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const azureConfigured = ref<boolean | null>(null)
  const azureLoginUrl = ref<string | null>(null)

  // Scope Testing Mode State (admin feature)
  const isTestingMode = ref(false)
  const testingScopes = ref<string[]>([])

  // Restore testing mode from sessionStorage on init
  const savedTestingMode = sessionStorage.getItem('scopeTestingMode')
  if (savedTestingMode) {
    try {
      const parsed = JSON.parse(savedTestingMode)
      isTestingMode.value = parsed.enabled || false
      testingScopes.value = parsed.scopes || []
    } catch {
      // Invalid data, ignore
    }
  }

  // Getters
  const isAuthenticated = computed(() => !!user.value && !!accessToken.value)

  // Check if user is admin (has wildcard scope in their REAL scopes)
  const isAdmin = computed(() => user.value?.scopes.includes('*') || false)

  // Get effective scopes (testing scopes if in testing mode, otherwise real scopes)
  const effectiveScopes = computed(() =>
    isTestingMode.value ? testingScopes.value : user.value?.scopes || []
  )

  /**
   * Check if user has a specific scope.
   * Implements fallback logic matching backend:
   * 1. Check for admin wildcard (*)
   * 2. Check for exact scope match
   * 3. For folder-scoped resources, fall back to global scope
   *    e.g., workflows:esg:read falls back to workflows:read
   *
   * Scope format: resource:folder:action
   * - workflows:esg:read - read workflows in 'esg' folder
   * - workflows:demo:execute - execute workflows in 'demo' folder
   * - templates:esg:write - write templates in 'esg' folder
   */
  const hasScope = (scope: string): boolean => {
    if (!user.value) return false

    const scopes = effectiveScopes.value
    return scopeMatches(scopes, scope)
  }

  /**
   * Check if any user scope satisfies the required scope.
   * Supports wildcards: *, topic:*, topic:tag:*
   * Supports 2-part fallback: topic:action matches topic:tag:action
   */
  const scopeMatches = (scopes: string[], required: string): boolean => {
    const requiredParts = required.split(':')
    for (const scope of scopes) {
      // Global admin wildcard
      if (scope === '*') return true
      // Exact match
      if (scope === required) return true

      const scopeParts = scope.split(':')
      // topic:* matches any topic:... scope
      if (scopeParts.length === 2 && scopeParts[1] === '*' && requiredParts[0] === scopeParts[0]) return true
      // topic:tag:* matches topic:tag:action
      if (scopeParts.length === 3 && scopeParts[2] === '*' && requiredParts.length >= 2
          && scopeParts[0] === requiredParts[0] && scopeParts[1] === requiredParts[1]) return true
      // 2-part fallback: topic:action matches topic:tag:action
      if (scopeParts.length === 2 && requiredParts.length === 3
          && scopeParts[0] === requiredParts[0] && scopeParts[1] === requiredParts[2]) return true
    }
    return false
  }

  /**
   * Check if user has access to a specific folder for a resource/action.
   * Returns true if user has:
   * - Admin wildcard (*)
   * - Global scope (resource:action) - access to ALL folders
   * - Folder-specific scope (resource:folder:action)
   *
   * @param resource - 'workflows' or 'templates'
   * @param folder - The workflow folder name (e.g., 'esg', 'demo')
   * @param action - 'read', 'write', 'execute', etc.
   */
  const hasFolderScope = (resource: string, folder: string | undefined | null, action: string): boolean => {
    if (!user.value) return false

    // Build the most specific scope and let scopeMatches handle fallbacks/wildcards
    const scope = folder ? `${resource}:${folder}:${action}` : `${resource}:${action}`
    return scopeMatches(effectiveScopes.value, scope)
  }

  /**
   * Get list of folders user has access to for a given resource/action.
   * Returns null if user has global access (all folders).
   * Returns empty array if user has no access.
   * Returns array of folder names if user has folder-specific access.
   *
   * @param resource - 'workflows' or 'templates'
   * @param action - 'read', 'write', 'execute', etc.
   */
  const getAccessibleFolders = (resource: string, action: string): string[] | null => {
    if (!user.value) return []

    const scopes = effectiveScopes.value

    // Check for global access patterns
    for (const scope of scopes) {
      if (scope === '*') return null                           // Admin wildcard
      if (scope === `${resource}:*`) return null               // Topic wildcard
      if (scope === `${resource}:${action}`) return null       // Global topic:action
    }

    // Extract folder-specific scopes
    const folders: string[] = []
    const pattern = new RegExp(`^${resource}:([^:*]+):(?:${action}|\\*)$`)

    for (const scope of scopes) {
      const match = scope.match(pattern)
      if (match) {
        folders.push(match[1])
      }
    }

    return folders
  }

  /**
   * Check if user has any access to a resource/action (global or folder-specific).
   * Useful for showing/hiding UI elements like upload buttons.
   *
   * @param resource - 'workflows' or 'templates'
   * @param action - 'read', 'write', 'execute', etc.
   */
  const hasAnyFolderAccess = (resource: string, action: string): boolean => {
    const folders = getAccessibleFolders(resource, action)
    // null means global access, array means specific folders (or empty = no access)
    return folders === null || folders.length > 0
  }

  // Get all favorite workflows
  const favoriteWorkflows = computed(() => {
    return user.value?.attributes?.favorite_workflows || {}
  })

  // Check if a specific workflow is favorited
  const isWorkflowFavorited = (engineType: string, workflowId: string): boolean => {
    const favorites = user.value?.attributes?.favorite_workflows
    if (!favorites) return false
    return favorites[engineType]?.includes(workflowId) || false
  }

  // Actions
  async function login(email: string, password: string): Promise<boolean> {
    isLoading.value = true
    error.value = null

    try {
      const response = await api.post<LoginResponse>('/api/auth/login', { email, password })

      accessToken.value = response.data.access_token
      user.value = response.data.user

      // Store token in localStorage for persistence
      localStorage.setItem('accessToken', response.data.access_token)

      // Sync token with API client
      api.setAccessToken(response.data.access_token)

      // Schedule proactive refresh
      scheduleTokenRefresh(response.data.access_token)

      return true
    } catch (err: any) {
      error.value = err.response?.data?.detail || 'Login failed'
      return false
    } finally {
      isLoading.value = false
    }
  }

  async function logout(): Promise<void> {
    isLoading.value = true
    scheduleTokenRefresh(null)

    try {
      // Let the browser receive the rotated cookie before revoking it.
      let refreshWaitTimerId: ReturnType<typeof setTimeout> | null = null
      try {
        await Promise.race([
          api.waitForPendingRefresh().catch(() => false),
          new Promise<void>(resolve => {
            refreshWaitTimerId = setTimeout(resolve, LOGOUT_REFRESH_WAIT_MS)
          })
        ])
      } finally {
        if (refreshWaitTimerId !== null) clearTimeout(refreshWaitTimerId)
      }
      await api.post('/api/auth/logout')
    } catch {
      // Ignore errors during logout
    } finally {
      // Clear state regardless of API result
      user.value = null
      accessToken.value = null
      localStorage.removeItem('accessToken')

      // Clear API client token
      api.setAccessToken(null)

      // Cancel scheduled refresh
      scheduleTokenRefresh(null)

      isLoading.value = false
    }
  }

  async function checkAuth(): Promise<boolean> {
    if (!accessToken.value) {
      return false
    }

    isLoading.value = true
    error.value = null

    try {
      const response = await api.get<User>('/api/auth/me')
      user.value = response.data
      return true
    } catch {
      // The API client already tried renewal and at most one repeat.
      user.value = null
      return false
    } finally {
      isLoading.value = false
    }
  }

  function refreshToken(): Promise<boolean> {
    return api.refreshToken()
  }

  function setAccessToken(token: string | null): void {
    accessToken.value = token
    if (token) {
      localStorage.setItem('accessToken', token)
    } else {
      localStorage.removeItem('accessToken')
    }
    // Sync with API client
    api.setAccessToken(token)
    // Reschedule proactive refresh
    scheduleTokenRefresh(token)
  }

  // Refresh user data from API (including attributes/favorites)
  async function refreshUserData(): Promise<boolean> {
    if (!accessToken.value) return false

    try {
      const response = await api.get<User>('/api/auth/me')
      user.value = response.data
      return true
    } catch {
      return false
    }
  }

  // Update local favorite status (for optimistic updates)
  function updateLocalFavorite(engineType: string, workflowId: string, isFavorited: boolean): void {
    if (!user.value) return

    // Ensure attributes and favorite_workflows exist
    if (!user.value.attributes) {
      user.value.attributes = {}
    }
    if (!user.value.attributes.favorite_workflows) {
      user.value.attributes.favorite_workflows = {}
    }
    if (!user.value.attributes.favorite_workflows[engineType]) {
      user.value.attributes.favorite_workflows[engineType] = []
    }

    const favorites = user.value.attributes.favorite_workflows[engineType]

    if (isFavorited && !favorites.includes(workflowId)) {
      favorites.push(workflowId)
    } else if (!isFavorited) {
      const index = favorites.indexOf(workflowId)
      if (index > -1) {
        favorites.splice(index, 1)
      }
    }
  }

  // Check if Azure/Entra authentication is configured
  async function checkAzureStatus(): Promise<boolean> {
    try {
      const response = await api.get<AzureStatusResponse>('/api/auth/azure/status')
      azureConfigured.value = response.data.configured
      azureLoginUrl.value = response.data.login_url
      return response.data.configured
    } catch {
      azureConfigured.value = false
      azureLoginUrl.value = null
      return false
    }
  }

  // Exchange existing session cookie for JWT tokens (used after Azure login)
  async function exchangeSessionForJwt(): Promise<boolean> {
    isLoading.value = true
    error.value = null

    try {
      const response = await api.post<LoginResponse>('/api/auth/session-exchange')

      accessToken.value = response.data.access_token
      user.value = response.data.user

      // Store token in localStorage for persistence
      localStorage.setItem('accessToken', response.data.access_token)

      // Sync token with API client
      api.setAccessToken(response.data.access_token)

      // Schedule proactive refresh
      scheduleTokenRefresh(response.data.access_token)

      return true
    } catch (err: any) {
      error.value = err.response?.data?.detail || 'Session exchange failed'
      return false
    } finally {
      isLoading.value = false
    }
  }

  // Initiate Azure login flow (redirects to Microsoft)
  function initiateAzureLogin(returnUrl?: string): void {
    if (!azureLoginUrl.value) {
      error.value = 'Azure login is not configured'
      return
    }

    // Store the return URL so we can redirect after login
    const redirect = returnUrl || window.location.pathname
    sessionStorage.setItem('azure_return_url', redirect)

    // Redirect to Azure login with return URL pointing to SPA
    const loginUrl = new URL(azureLoginUrl.value, window.location.origin)
    loginUrl.searchParams.set('redirect_url', '/sso/callback')

    window.location.href = loginUrl.toString()
  }

  // Enable scope testing mode (admin only)
  function enableScopeTesting(scopes: string[]): boolean {
    // Only admins can use testing mode
    if (!isAdmin.value) return false

    testingScopes.value = scopes
    isTestingMode.value = true

    // Persist to sessionStorage
    sessionStorage.setItem('scopeTestingMode', JSON.stringify({
      enabled: true,
      scopes: scopes
    }))

    return true
  }

  // Disable scope testing mode
  function disableScopeTesting(): void {
    isTestingMode.value = false
    testingScopes.value = []
    sessionStorage.removeItem('scopeTestingMode')
  }

  // Update testing scopes without disabling
  function updateTestingScopes(scopes: string[]): void {
    if (!isTestingMode.value) return
    testingScopes.value = scopes
    sessionStorage.setItem('scopeTestingMode', JSON.stringify({
      enabled: true,
      scopes: scopes
    }))
  }

  // --- Proactive token refresh timer (Option A) ---
  let refreshTimerId: ReturnType<typeof setTimeout> | null = null

  /** Schedule a token refresh ~60 seconds before the access token expires. */
  function scheduleTokenRefresh(token: string | null, retryDelay?: number): void {
    // Clear any existing timer
    if (refreshTimerId !== null) {
      clearTimeout(refreshTimerId)
      refreshTimerId = null
    }

    if (!token) return

    const payload = decodeJwtPayload(token)
    if (!payload || typeof payload.exp !== 'number') return

    const expiresAt = payload.exp * 1000 // ms
    const now = Date.now()
    // Refresh 60 seconds before expiry, but at least 5 seconds from now
    const delay = retryDelay ?? Math.max(expiresAt - now - 60_000, 5_000)

    refreshTimerId = setTimeout(async () => {
      refreshTimerId = null
      const refreshed = await refreshToken()
      if (refreshed && !user.value && accessToken.value) {
        await checkAuth()
      }
      // A rejected refresh clears the token; temporary failures get another attempt.
      if (!refreshed && accessToken.value && refreshTimerId === null) {
        scheduleTokenRefresh(accessToken.value, TOKEN_REFRESH_RETRY_DELAY_MS)
      }
    }, delay)
  }

  // Renewals from requests and the timer both update the store and its next timer.
  api.onAccessTokenChanged(token => {
    accessToken.value = token
    scheduleTokenRefresh(token)
  })

  // Start the timer for the current token (on store init / page reload)
  scheduleTokenRefresh(accessToken.value)

  /**
   * Ensure the token is valid, then open a download URL.
   * If the token is expired the API client will attempt a refresh.
   * If the refresh token is rejected, the session-expired modal is shown.
   */
  async function safeDownload(url: string): Promise<void> {
    const valid = await api.ensureValidToken()
    if (valid) {
      window.open(url, '_blank')
    }
    // Rejected refresh tokens are reported by the API client.
  }

  return {
    // State
    user,
    accessToken,
    isLoading,
    error,
    azureConfigured,
    azureLoginUrl,

    // Scope Testing State
    isTestingMode,
    testingScopes,

    // Getters
    isAuthenticated,
    isAdmin,
    effectiveScopes,
    hasScope,
    hasFolderScope,
    getAccessibleFolders,
    hasAnyFolderAccess,
    favoriteWorkflows,
    isWorkflowFavorited,

    // Actions
    login,
    logout,
    checkAuth,
    refreshToken,
    setAccessToken,
    refreshUserData,
    updateLocalFavorite,
    checkAzureStatus,
    exchangeSessionForJwt,
    initiateAzureLogin,

    // Scope Testing Actions
    enableScopeTesting,
    disableScopeTesting,
    updateTestingScopes,

    // Token management
    safeDownload
  }
})
